import { CommonModule, DatePipe, TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HxIcon } from '../icon/icon';
import { HxDropdownComponent } from '../select/dropdown/dropdown.component';
import { HxOption } from '../select/shared/option.model';
import { FilterOption, ReleaseNoteItemType, ReleaseNoteItemTypeEnum, ReleaseNoteView } from './types';

/**
 * Release Notes Renderer Component
 *
 * Displays a timeline of release notes with filtering capabilities. Used for both user-facing release history and admin
 * preview.
 *
 * @example
 *   ```html
 *   <hx-release-notes-renderer
 *     [releaseNotes]="notes"
 *     [showFilters]="true"
 *     [showLatestBadge]="true"
 *   />
 *   ```;
 */
@Component({
  selector: 'hx-release-notes-renderer',
  standalone: true,
  imports: [CommonModule, HxIcon, HxDropdownComponent, DatePipe, TitleCasePipe],
  templateUrl: './release-notes-renderer.html',
  styleUrls: ['./release-notes-renderer.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxReleaseNotesRenderer {
  private readonly sanitizer = inject(DomSanitizer);

  /** Expose enum to template */
  protected readonly ReleaseNoteItemTypeEnum = ReleaseNoteItemTypeEnum;

  /** Array of release notes to display */
  releaseNotes = input<ReleaseNoteView[]>([]);

  /** Show release selector dropdown */
  showDropdown = input<boolean>(true);

  /** Show filter chips (All/New/Improved/Fixed/Removed) */
  showFilters = input<boolean>(true);

  /** Show "Latest" badge on most recent release */
  showLatestBadge = input<boolean>(true);

  /** Preview mode (admin config preview) */
  isPreviewMode = input<boolean>(false);

  /** Max items to show per release (0 = unlimited) */
  maxItemsPerRelease = input<number>(0);

  /** Currently selected filter */
  selectedFilter = signal<'all' | ReleaseNoteItemType>('all');

  /** Currently selected release ID */
  selectedReleaseId = signal<number | null>(null);

  /** Track if we've initialized the selected release */
  private hasInitialized = false;

  /** Dropdown options for release selection (individual releases only) */
  releaseOptions = computed<HxOption<number | null>[]>(() =>
    this.releaseNotes().map((release) => ({
      id: release.releaseNoteId,
      label: release.version ? `${release.version} – ${release.releaseName}` : release.releaseName,
      value: release.releaseNoteId,
      subtitle: release.releaseDate
        ? new Date(release.releaseDate).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })
        : undefined,
    }))
  );

  /** Get the latest release based on release date */
  private getLatestRelease(): ReleaseNoteView | null {
    const releases = this.releaseNotes();
    if (releases.length === 0) {
      return null;
    }

    // Find release with most recent date
    return releases.reduce(
      (latest, current) => {
        if (!latest || !current.releaseDate) {
          return latest || current;
        }
        if (!latest.releaseDate) {
          return current;
        }

        const latestDate = new Date(latest.releaseDate);
        const currentDate = new Date(current.releaseDate);

        return currentDate > latestDate ? current : latest;
      },
      releases[0] as ReleaseNoteView | null
    );
  }

  /** Initialize selected release to latest (by date) when releases load */
  private readonly initializeSelectedRelease = effect(() => {
    const releases = this.releaseNotes();

    // Auto-select latest release only once on initial load
    if (!this.hasInitialized && releases.length > 0) {
      const latestRelease = this.getLatestRelease();
      if (latestRelease) {
        this.selectedReleaseId.set(latestRelease.releaseNoteId);
        this.hasInitialized = true;
      }
    }
  });

  /** Custom display text for dropdown (without date) */
  dropdownDisplayText = computed(() => {
    const selectedId = this.selectedReleaseId();

    const selectedRelease = this.releaseNotes().find((r) => r.releaseNoteId === selectedId);
    if (!selectedRelease) {
      return '';
    }

    return selectedRelease.version
      ? `${selectedRelease.version} – ${selectedRelease.releaseName}`
      : selectedRelease.releaseName;
  });

  /** Check if a release is the latest one (by date) */
  isLatestRelease(releaseId: number): boolean {
    const latestRelease = this.getLatestRelease();
    return latestRelease?.releaseNoteId === releaseId;
  }

  /** Computed filtered items based on selected filter and release */
  filteredReleases = computed(() => {
    const filter = this.selectedFilter();
    const selectedId = this.selectedReleaseId();
    const isPreview = this.isPreviewMode();
    let releases = this.releaseNotes();

    // In preview mode, show all releases (skip ID filtering)
    // Otherwise, filter by selected release ID
    if (!isPreview && selectedId !== null) {
      releases = releases.filter((r) => r.releaseNoteId === selectedId);
    }

    // Then filter by item type
    if (filter === 'all') {
      return releases;
    }

    return releases
      .map((release) => ({
        ...release,
        items: release.items?.filter((item) => item.type === filter) ?? [],
      }))
      .filter((release) => release.items && release.items.length > 0);
  });

  /** Filter options for chips */
  filterOptions: FilterOption[] = [
    { value: 'all', label: 'All' },
    { value: ReleaseNoteItemTypeEnum.NEW, label: 'New', icon: 'ccs-sparkle' },
    { value: ReleaseNoteItemTypeEnum.IMPROVED, label: 'Improved', icon: 'ccs-arrow-up' },
    { value: ReleaseNoteItemTypeEnum.FIXED, label: 'Fixed', icon: 'ccs-wrench' },
    { value: ReleaseNoteItemTypeEnum.REMOVED, label: 'Removed', icon: 'ccs-trash' },
    { value: ReleaseNoteItemTypeEnum.INCIDENT, label: 'Incident', icon: 'ccs-exclamation-circle-fill' },
  ];

  /** Handle filter change */
  onFilterChange(filter: string): void {
    this.selectedFilter.set(filter as 'all' | ReleaseNoteItemType);
  }

  /** Handle release selection change */
  onReleaseChange(releaseId: number | number[] | null): void {
    // Handle single selection only (dropdown is not multi-select)
    const id = Array.isArray(releaseId) ? releaseId[0] : releaseId;
    this.selectedReleaseId.set(id ?? null);
  }

  /** Get icon for item type */
  getItemIcon(type: ReleaseNoteItemType): string {
    const iconMap: Record<ReleaseNoteItemType, string> = {
      [ReleaseNoteItemTypeEnum.NEW]: 'ccs-sparkle',
      [ReleaseNoteItemTypeEnum.IMPROVED]: 'ccs-arrow-up',
      [ReleaseNoteItemTypeEnum.FIXED]: 'ccs-wrench',
      [ReleaseNoteItemTypeEnum.REMOVED]: 'ccs-trash',
      [ReleaseNoteItemTypeEnum.INCIDENT]: 'ccs-exclamation-circle-fill',
    };
    return iconMap[type];
  }

  /** Sanitize HTML content to allow inline styles from Jodit editor */
  sanitizeContent(content: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }
}
